import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGetGroupsQuery, useDeleteGroupMutation } from "../../../services";
import { IGroup } from "../../../types";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";

const GroupList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Pagination state for infinite scroll
  const [page, setPage] = useState(1);
  const limit = 10;
  const [allGroups, setAllGroups] = useState<IGroup[]>([]);

  // Load from RTK with pagination
  const {
    data: groupsResponse,
    isLoading,
    isFetching,
    error,
  } = useGetGroupsQuery({ page, limit });
  const [deleteGroup, { isLoading: isDeleting }] = useDeleteGroupMutation();

  const [deleteTarget, setDeleteTarget] = useState<IGroup | null>(null);
  const openDelete = (row: IGroup) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      await deleteGroup(deleteTarget._id).unwrap();
      // Optimistically remove from local infinite-scroll list
      setAllGroups((prev) => prev.filter((g) => g._id !== deleteTarget._id));
    } catch (e) {
      console.error("Delete Group failed:", e);
    } finally {
      closeDelete();
    }
  };

  // Current page rows from API
  const currentPageRows: IGroup[] = Array.isArray(groupsResponse)
    ? (groupsResponse as IGroup[])
    : [];

  // Append new page data to allGroups
  React.useEffect(() => {
    if (!currentPageRows || currentPageRows.length === 0) return;
    setAllGroups((prev) =>
      page === 1 ? currentPageRows : [...prev, ...currentPageRows]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsResponse, page]);

  // Infinite scroll: load next page when reaching bottom
  React.useEffect(() => {
    const container = document.getElementById("group-table-scroll");
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 40
      ) {
        // Only fetch next page if not already loading and more data may be available
        if (!isFetching && currentPageRows.length === limit) {
          setPage((prev) => prev + 1);
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [currentPageRows.length, isFetching, limit]);

  // THEME CLASSES
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = "text-text";
  const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border-0 text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
      : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

  const thClass =
    theme === "dark"
      ? "px-6 py-3 text-left text-sm font-bold text-[#F3F3F6] tracking-wider border-b border-[#2A2A2A]"
      : "px-6 py-3 text-left text-sm font-bold text-gray-700 tracking-wider border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
      : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

  const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";

  const isSidebarOpen = useAppSelector(
    (state: RootState) => state.navbar.isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        <h1 className={`text-lg font-medium ${heading} mb-2`}>
          Group Overview
        </h1>
        <div className="text-sm flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to={adminRoutes.dashboard}
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          <span className="text-[#B6CB24] font-normal">All Groups</span>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
        <div className={cardHeader}>
          <span className="text-base font-medium">All Group details</span>

          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium"
            onClick={() => navigate(adminRoutes.groupCreate)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8.00012V16.0001M16 12.0001H8"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Create new Group
          </button>
        </div>

        <div
          className="relative overflow-x-auto"
          style={{ maxHeight: "500px", overflowY: "auto" }}
          id="group-table-scroll"
        >
          <table className="min-w-full">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: theme === "dark" ? "#1A1A1A" : "#fff",
              }}
            >
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Category</th>
                {/* <th className={thClass}>Metric</th> */}
                <th className={thClass}>Status</th>
                <th className={thClass}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading || (isFetching && page === 1) ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={5}>
                    Loading groups...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={5}>
                    Failed to load groups.
                  </td>
                </tr>
              ) : allGroups.length === 0 ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={5}>
                    No groups found.
                  </td>
                </tr>
              ) : (
                allGroups.map((r) => (
                  <tr
                    key={r._id}
                    className={
                      theme === "dark"
                        ? "hover:bg-[#222222]"
                        : "hover:bg-gray-50"
                    }
                  >
                    <td className={tdClass}>{r.name}</td>
                    <td className={tdClass}>
                      {typeof r.category === "object"
                        ? r.category?.name
                        : r.category}
                    </td>
                    {/* <td className={tdClass}>
                      {typeof r.metric === "object"
                        ? (r.metric as any)?.name
                        : r.metric}
                    </td> */}
                    <td className={tdClass}>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          r.status === "Active"
                            ? "bg-lime-500/10 text-lime-500"
                            : "bg-red-500/10 text-red-500"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-2">
                        <button
                          title="Edit"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() =>
                            navigate(
                              adminRoutes.groupEdit.replace(":groupid", r._id!)
                            )
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                              stroke="#C6D92C"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                              stroke="#F3F3F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          title="Delete"
                          className="p-1.5 rounded hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => openDelete(r)}
                          disabled={isDeleting}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                              fill="url(#paint0_linear_278_6482)"
                            />
                            <path
                              d="M9.5 16.5V10.5"
                              stroke="url(#paint1_linear_278_6482)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M14.5 16.5V10.5"
                              stroke="url(#paint2_linear_278_6482)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <defs>
                              <linearGradient
                                id="paint0_linear_278_6482"
                                x1="12"
                                y1="1.25"
                                x2="12"
                                y2="22.75"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                              <linearGradient
                                id="paint1_linear_278_6482"
                                x1="10"
                                y1="10.5"
                                x2="10"
                                y2="16.5"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                              <linearGradient
                                id="paint2_linear_278_6482"
                                x1="15"
                                y1="10.5"
                                x2="15"
                                y2="16.5"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            theme === "dark" ? "bg-black/60" : "bg-black/40"
          }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
              theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
            }`}
          >
            <button
              onClick={closeDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>
            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
                  theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                }`}
              >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round"/>
  <path d="M20 15L13 21.9995M20 22L13 15.0005" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round"/>
</svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Group</h3>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                Are you sure you want to delete "{deleteTarget.name}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={closeDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupList;
